/* eslint-disable no-unused-vars */

import {
  Box,
  Button,
  Container,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  makeStyles,
  MenuItem,
  OutlinedInput,
  Select,
  Switch,
  TextField
} from '@material-ui/core';
import React, { FC, useCallback, useEffect, useState } from 'react';
import GetAppIcon from '@material-ui/icons/GetApp';
import { downloadCSV } from 'src/utils';
import { useVouchers } from 'src/hooks/useVouchers';
import { unwrapResult } from '@reduxjs/toolkit';
import { useAppDispatch } from 'src/redux';
import { Voucher } from 'src/redux/slices/voucher/types';
import useResolution from 'src/hooks/useResolution';

const useStyles = makeStyles({
  customInput: {
    '&.MuiOutlinedInput-root': {
      '&.Mui-focused': {
        backgroundColor: 'transparent' // Remove the gray background on focus
      }
    },
    '& .MuiOutlinedInput-input': {
      backgroundColor: 'transparent !important' // Ensure input itself is transparent
    }
  }
});

export const Toolbar: FC = () => {
  const classes = useStyles();
  const { isMobile } = useResolution();
  const {
    getVoucherTypeOptions,
    typeOptions,
    vouchersList,
    getVouchers,
    isOptionsLoading,
    keyword,
    onChangeKeyword,
    voucherType,
    isListLoading,
    setVoucherType,
    saveCurrentFilters,
    appliedFilter,
    isActive,
    setIsActive,
    isGeneric,
    toggleGenericFilter,
    getVouchersCSV
  } = useVouchers();

  const [isExportLoading, setIsExportLoading] = useState<boolean>(false);

  // any intended
  const renderFilterValue = (selected: any) => {
    if (selected === 'all') return 'All';
    return selected === 'active' ? 'Active' : 'Inactive';
  };

  // any intended
  const renderTypeValue = (selected: any) => {
    if (selected === null) {
      return <span style={{ color: '#aaa' }}>Voucher Type</span>;
    }
    return selected;
  };

  const onClickDownload = useCallback(async () => {
    setIsExportLoading(true);
    const csv: Voucher[] = await getVouchersCSV();
    if (!csv[0]) {
      console.error('No data found for exporting');
      setIsExportLoading(false);
      return;
    }

    const formattedCSV = csv.map((i) => ({
      id: i?.id || '',
      code: i?.code || '',
      type: i?.discount_type || '', // Renaming `discount_type` to `type`
      affiliate: i?.affiliate?.full_name || '',
      start_date: i?.start_date || '',
      end_date: i?.end_date || '',
      consumed: i?.consumed || '',
      amount: i?.amount || ''
    }));

    downloadCSV(formattedCSV, 'Vouchers.csv');
    setIsExportLoading(false);
  }, [getVouchersCSV]);

  useEffect(() => {
    getVoucherTypeOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getVouchers(appliedFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilter]);

  return (
    <Box id="toolbar" style={{ padding: '1em 2em' }}>
      <Grid container style={{ columnGap: '1em', rowGap: '1em' }}>
        <Grid item md={4} xs={12} sm={5}>
          <TextField
            id="search"
            fullWidth
            required
            variant="outlined"
            size="small"
            onChange={(e) => onChangeKeyword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && saveCurrentFilters()}
            placeholder="Search code"
            value={keyword}
          />
        </Grid>
        <Grid item md={3} xs={12} sm>
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel id="is-active-label">Type</InputLabel>
            <Select
              disabled={isOptionsLoading || !typeOptions?.[0]}
              fullWidth
              id="voucher-type"
              value={voucherType}
              displayEmpty
              input={
                <OutlinedInput
                  classes={{ root: classes.customInput }}
                  label="Type"
                />
              }
              style={{ height: '2.5em' }}
              renderValue={renderTypeValue}
            >
              {typeOptions[0]
                ? typeOptions?.map((option, ind) => (
                    <MenuItem
                      key={ind}
                      onClick={() => {
                        setVoucherType((prev) =>
                          prev === option ? '' : option
                        );
                      }}
                    >
                      {option}
                    </MenuItem>
                  ))
                : null}
            </Select>
          </FormControl>
        </Grid>
        <Grid item md={3} xs={12} sm>
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel id="is-active-label">Status</InputLabel>
            <Select
              labelId="is-active-label"
              fullWidth
              name="is_active"
              value={isActive}
              displayEmpty
              input={
                <OutlinedInput
                  classes={{ root: classes.customInput }}
                  label="Status"
                />
              }
              style={{ height: '2.5em' }}
              renderValue={renderFilterValue}
            >
              <MenuItem onClick={() => setIsActive('all')}>All</MenuItem>
              <MenuItem
                onClick={() => {
                  setIsActive('active');
                }}
              >
                Active
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setIsActive('inactive');
                }}
              >
                Inactive
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm>
          <Box display="flex" justifyContent={isMobile ? 'end' : 'start'}>
            <Button
              disabled={isListLoading}
              variant="contained"
              color="primary"
              fullWidth={isMobile ? true : undefined}
              onClick={saveCurrentFilters}
              style={{ height: '2.8em', width: isMobile ? '100%' : 180 }}
            >
              Search
            </Button>
          </Box>
        </Grid>
        <Grid
          item
          xs={12}
          style={{ display: 'flex', justifyContent: 'space-between' }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={isGeneric}
                color="primary"
                onChange={(_, checked) => toggleGenericFilter(checked)}
              />
            }
            label="Generic Vouchers"
          />
          <Button
            disabled={isExportLoading || isListLoading || !vouchersList?.[0]}
            fullWidth={isMobile ? true : undefined}
            onClick={onClickDownload}
            variant="outlined"
            startIcon={<GetAppIcon />}
          >
            Export as CSV
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};
